import { useCallback, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface ScrollRevealOptions {
  y?: number;
  x?: number;
  duration?: number;
  delay?: number;
  stagger?: number;
}

export function useScrollReveal<T extends HTMLElement>(
  options: ScrollRevealOptions = {},
) {
  const tweenRef = useRef<gsap.core.Tween | null>(null);
  const { y = 40, x = 0, duration = 0.8, delay = 0, stagger = 0.1 } = options;

  const ref = useCallback(
    (el: T | null) => {
      if (!el) {
        tweenRef.current?.kill();
        return;
      }

      const prefersReduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      if (prefersReduced) return;

      const children = el.querySelectorAll("[data-reveal]");
      const targets = children.length > 0 ? children : el;

      gsap.set(targets, { opacity: 0, y, x });

      tweenRef.current = gsap.to(targets, {
        opacity: 1,
        y: 0,
        x: 0,
        duration,
        delay,
        stagger,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      });
    },
    [y, x, duration, delay, stagger],
  );

  return ref;
}
